/**
 * MiniUnitPairCard — split-grade mini-unit surfacing (Lane A).
 *
 * Informational only. Shows how one unit slot divides across the two grades.
 * The card COLOUR follows the unit's notice tier, not the pair status, so it
 * agrees with the notice above it:
 *   Tier 1 unit → amber   (a genuine divided stretch)
 *   Tier 2 unit → steel   (same lesson, two tasks)
 *   paired row, or no tier → mint
 * The pill TEXT still reflects the row status (Paired / Partial / Unpaired).
 */
import React from "react";
import { AlertTriangle, ArrowLeftRight, Check } from "lucide-react";

const STATUS_LABEL = { paired: "Paired", partial: "Partial", unpaired: "Unpaired" };
const STATUS_ICON = { paired: Check, partial: ArrowLeftRight, unpaired: AlertTriangle };

const MINT = { pill: "bg-[#96ded1]/30 text-[#12463b]", edge: "border-[#96ded1]/60" };
const AMBER = { pill: "bg-amber-100 text-amber-800", edge: "border-amber-300" };
const STEEL = { pill: "bg-[#4a6984]/12 text-[#1c2d3d]", edge: "border-[#4a6984]/30" };

const Side = ({ side }) => (
  <div className="flex-1 min-w-0">
    <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#4a6984]/70">
      {side.grade}
    </p>
    {side.label ? (
      <>
        <p className="mt-0.5 text-[11px] font-bold text-[#4a6984]/80">{side.label}</p>
        <p className="font-extrabold text-sm text-[#1c2d3d] tracking-tight leading-tight">
          {side.name}
        </p>
      </>
    ) : (
      <p className="mt-0.5 text-sm italic text-[#4a6984]/70 leading-snug">
        {side.note ? `(${side.note})` : "— no mini-unit —"}
      </p>
    )}
  </div>
);

const MiniUnitPairCard = ({ pair, unitNumber, noticeTier }) => {
  // Colour follows the tier; paired rows are always mint.
  let theme = MINT;
  if (pair.status !== "paired") {
    theme = noticeTier === "runs_divided" ? AMBER : noticeTier === "plan_two" ? STEEL : AMBER;
  }
  const Icon = STATUS_ICON[pair.status] || Check;
  const label = STATUS_LABEL[pair.status] || "Paired";
  const key = pair.lower?.label || pair.upper?.label || `${unitNumber}`;
  return (
    <div
      data-testid={`math-engine-pair-${unitNumber}-${key}`}
      className={`rounded-xl border-2 bg-white p-4 ${theme.edge}`}
    >
      <div className="flex items-center justify-between gap-2 mb-3">
        <span
          data-testid={`math-engine-pair-status-${pair.status}`}
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${theme.pill}`}
        >
          <Icon className="h-3 w-3" /> {label}
        </span>
        {pair.reason ? (
          <span className="text-[11px] font-semibold text-[#4a6984]/70 text-right truncate">
            {pair.reason}
          </span>
        ) : null}
      </div>
      <div className="flex items-stretch gap-3">
        <Side side={pair.lower} />
        <div className="w-px bg-[#4a6984]/15" />
        <Side side={pair.upper} />
      </div>
    </div>
  );
};

export default MiniUnitPairCard;
